import {
  EscHotKeyIcon,
  ReturnHotKeyIcon,
} from '@modules/common/components/HotKeyIcons';
import { useGetDashboardTypeDisplayNameFromURLParam } from '@modules/common/hooks';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  DialogActions,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

interface SaveFeedAndAddToDashboardFormFooterProps {
  isSaveButtonDisabled?: boolean;
  isLoading: boolean;
  shouldAddToNewDashboard?: boolean;
  isDashboardExists?: boolean;
  newDashboardName: string;
  step: number;
  onCancel: () => void;
  onNext: () => void;
  onBack: () => void;
  saveButtonLabel?: string;
  shouldSkipBackButton?: boolean;
}

export function SaveFeedAndAddToDashboardFormFooter({
  isSaveButtonDisabled,
  isLoading,
  shouldAddToNewDashboard,
  isDashboardExists,
  newDashboardName,
  step,
  onCancel,
  onNext,
  onBack,
  saveButtonLabel,
  shouldSkipBackButton = false,
}: SaveFeedAndAddToDashboardFormFooterProps) {
  const dashboardTypeDisplayName = useGetDashboardTypeDisplayNameFromURLParam();

  const rendetNextButton = () => {
    const nextButton = (
      <Button
        size="small"
        endIcon={<ArrowForward />}
        variant="contained"
        onClick={onNext}
        color="primary"
        disabled={isSaveButtonDisabled}
      >
        Next
      </Button>
    );

    return isSaveButtonDisabled ? (
      <Tooltip
        title={`Please add a name to save the ${
          shouldAddToNewDashboard && !newDashboardName?.trim()
            ? 'Dashboard'
            : 'Feed'
        }`}
        placement="top"
        arrow
      >
        <Box>{nextButton}</Box>
      </Tooltip>
    ) : (
      nextButton
    );
  };

  const renderSubmitButton = () => {
    return (
      <LoadingButton
        variant="contained"
        type="submit"
        loading={isLoading}
        loadingPosition="end"
        endIcon={
          shouldSkipBackButton ? undefined : (
            <ReturnHotKeyIcon isDisabled={isSaveButtonDisabled} />
          )
        }
      >
        {saveButtonLabel || `Add To ${dashboardTypeDisplayName}`}
      </LoadingButton>
    );
  };

  const getPrimaryButton = () => {
    if (shouldSkipBackButton) {
      return renderSubmitButton();
    }

    return step === 0 ? rendetNextButton() : renderSubmitButton();
  };

  const getSecondaryButton = () => {
    if (shouldSkipBackButton) {
      return null;
    }

    return step === 1 ? (
      <Button
        size="small"
        startIcon={<ArrowBack />}
        variant="outlined"
        onClick={onBack}
      >
        Back
      </Button>
    ) : null;
  };

  return (
    <DialogActions>
      <Stack
        direction="row"
        m={2}
        justifyContent={shouldSkipBackButton ? 'flex-end' : 'space-between'}
        alignItems="center"
        width="100%"
      >
        {shouldSkipBackButton ? null : (
          <Typography variant="caption">Step {step + 1} of 2</Typography>
        )}
        <Stack direction="row" spacing={2}>
          <Button
            size="small"
            variant="outlined"
            onClick={onCancel}
            type="button"
            disabled={isLoading}
            endIcon={
              <EscHotKeyIcon
                isDisabled={
                  isLoading || isDashboardExists || shouldAddToNewDashboard
                }
              />
            }
          >
            Cancel
          </Button>
          {getSecondaryButton()}
          {getPrimaryButton()}
        </Stack>
      </Stack>
    </DialogActions>
  );
}
